import React from 'react';
import {
  FaBuilding,
  FaProjectDiagram,
  FaFlag,
  FaFileInvoiceDollar,
  FaMapMarkerAlt,
  FaPlus,
} from 'react-icons/fa';
import { Shift } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { ProjectSite, JobDetail, ProjectMilestone } from '../services/PlantafelService';
import styles from './PlantafelSidePanel.module.css';

interface SidePanelTaskInfoProps {
  task: CrmTask | null;
  shift: Shift | null;
  jobDetail: JobDetail | null;
  projectId: number | null | undefined;
  statusColors: { bg: string; text: string } | null;
  priorityColors: { bg: string; text: string } | null;
  sites: ProjectSite[];
  sitesLoading: boolean;
  selectedSiteId: number | null;
  savingSite: boolean;
  projectMilestones: ProjectMilestone[];
  milestonesLoading: boolean;
  savingMilestone: boolean;
  handleOpenPartner: () => void;
  handleOpenProject: () => void;
  handleOpenOffer: () => void;
  handleSaveSite: (siteId: number | null) => void;
  handleSaveMilestone: (milestoneId: number | null) => void;
  navigate: (path: string) => void;
}

export function SidePanelTaskInfo({
  task, shift, jobDetail, projectId, statusColors, priorityColors,
  sites, sitesLoading, selectedSiteId, savingSite,
  projectMilestones, milestonesLoading, savingMilestone,
  handleOpenPartner, handleOpenProject, handleOpenOffer,
  handleSaveSite, handleSaveMilestone, navigate,
}: SidePanelTaskInfoProps): React.JSX.Element | null {
  return (
    <>
      {/* Title, Project, Customer & Status */}
      {task && (
        <div style={{ marginBottom: '20px' }}>
          {renderCustomerOfferLinks()}
          {renderProjectLink()}
          {renderStatusBadges()}
          {task.description && (
            <p className={styles.descriptionText}>{task.description}</p>
          )}
        </div>
      )}

      {/* Site Selection for Job Shifts */}
      {shift?.is_job_shift && projectId && (
        <div className={styles.section} style={{ marginBottom: '16px' }}>
          <div className={styles.sectionTitle}>
            <FaMapMarkerAlt size={11} style={{ marginRight: '6px' }} />
            Standort (Site)
          </div>
          {renderSiteSelect()}
        </div>
      )}

      {/* Editable Milestone Section */}
      {shift?.is_job_shift && projectId && (
        <div data-testid="sidepanel-milestone-section" className={styles.section} style={{ marginBottom: '16px' }}>
          <div className={styles.sectionTitle}>
            <FaFlag size={11} style={{ marginRight: '6px' }} />
            Zahlungs-Milestone
          </div>
          {renderMilestoneSelect()}
        </div>
      )}
    </>
  );

  function renderCustomerOfferLinks() {
    if (!task) return null;
    const offerNumber = task?.offer_number
      || (task as any)?.offer?.offer_number
      || (shift as any)?.offer_number
      || jobDetail?.offer_number;
    const offerId = task?.offer || task?.offer_id
      || (task as any)?.offer?.id
      || (shift as any)?.offer || (shift as any)?.offer_id
      || jobDetail?.offer_id;
    const partnerName = task.partner_name || (shift as any)?.partner_name;

    return (
      <div className={styles.linkRow} style={{ marginBottom: '6px' }}>
        {partnerName && (
          <div
            onClick={handleOpenPartner}
            className={styles.linkItem}
            title="Kunde öffnen"
          >
            <FaBuilding size={10} color="#888" />
            <span className={styles.linkText}>{partnerName}</span>
          </div>
        )}
        {(offerNumber || offerId) && (
          <div
            onClick={() => offerId && navigate(`/offers/${offerId}`)}
            className={styles.linkItem}
            style={{ cursor: offerId ? 'pointer' : 'default' }}
            title={offerId ? 'Angebot öffnen' : undefined}
          >
            <FaFileInvoiceDollar size={10} color="#888" />
            <span className={offerId ? styles.linkText : styles.linkTextDisabled}>
              {offerNumber || `#${offerId}`}
            </span>
          </div>
        )}
      </div>
    );
  }

  function renderProjectLink() {
    if (!task) return null;
    if (!(task.project_name || shift?.project_name)) return null;
    return (
      <div
        onClick={handleOpenProject}
        className={styles.projectLink}
        title="Projekt öffnen"
      >
        <FaProjectDiagram size={10} color="#888" />
        <span className={styles.linkText}>
          {task.project_name || shift?.project_name}
        </span>
      </div>
    );
  }

  function renderStatusBadges() {
    if (!task) return null;
    return (
      <div className={styles.statusBadgeRow}>
        {statusColors && (
          <span
            className={styles.statusBadge}
            style={{ background: statusColors.bg, color: statusColors.text }}
          >
            {task.status}
          </span>
        )}
        {priorityColors && (
          <span
            className={styles.priorityBadge}
            style={{ background: priorityColors.bg, color: priorityColors.text }}
          >
            <FaFlag size={8} />
            {task.priority}
          </span>
        )}
      </div>
    );
  }

  function renderSiteSelect() {
    if (sitesLoading) {
      return <div className={styles.loadingText}>Laden...</div>;
    }
    if (sites.length === 0) {
      return (
        <div className={styles.noItemsCenter}>
          <div className={styles.noItemsText}>Keine Sites im Projekt vorhanden</div>
          <button onClick={handleOpenProject} className={styles.createLinkButton}>
            <FaPlus size={10} />
            Sites im Projekt anlegen
          </button>
        </div>
      );
    }
    return (
      <select
        value={selectedSiteId || ''}
        onChange={(e) => {
          const val = e.target.value ? parseInt(e.target.value, 10) : null;
          handleSaveSite(val);
        }}
        disabled={savingSite}
        className={`${styles.selectInput} ${savingSite ? styles.selectInputDisabled : styles.selectInputActive}`}
      >
        <option value="">Kein Standort ausgewählt</option>
        {sites.map((site) => (
          <option key={site.id} value={site.id}>
            {site.name}
            {site.address && ` \u2013 ${site.address}`}
          </option>
        ))}
      </select>
    );
  }

  function renderMilestoneSelect() {
    if (milestonesLoading) {
      return <div className={styles.loadingText}>Laden...</div>;
    }
    if (projectMilestones.length === 0) {
      return (
        <div className={styles.noItemsCenter}>
          <div className={styles.noItemsText}>Keine Milestones im Projekt definiert</div>
          <button onClick={handleOpenProject} className={styles.createLinkButton}>
            <FaPlus size={10} />
            Milestones im Projekt anlegen
          </button>
        </div>
      );
    }
    return (
      <select
        data-testid="sidepanel-milestone-select"
        value={jobDetail?.payment_milestone_id || ''}
        onChange={(e) => {
          const val = e.target.value ? parseInt(e.target.value, 10) : null;
          handleSaveMilestone(val);
        }}
        disabled={savingMilestone}
        className={`${styles.selectInput} ${savingMilestone ? styles.selectInputDisabled : styles.selectInputActive}`}
      >
        <option value="">Kein Milestone zugeordnet</option>
        {projectMilestones.map((milestone) => (
          <option key={milestone.id} value={milestone.id}>
            {milestone.label} ({milestone.percent}%)
          </option>
        ))}
      </select>
    );
  }
}
